import { useState, useMemo, Fragment } from "react";
import { Link } from "react-router";
import { Minus, Plus, Pencil, Square, ArrowUp, ArrowDown, Printer, Search, Trash2 } from "lucide-react";
import PageHeader from "./PageHeader";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "./ui/table";
import useFirebaseInventory from "../hooks/useFirebaseInventory";
import { useAllFirebaseGlassData } from "../hooks/useFirebaseGlassData";
import InlineMaterialForm from "../components/InlineMaterialForm";
import useInventoryActions from "../hooks/useInventoryActions";
import { glassImage } from "../helpers/logoHelpers";
import { getHexColor } from "../helpers/glassHelpers";
import { groupInventoryItems } from "../helpers/inventoryHelpers";
import type { EnrichedInventoryItem } from "../helpers/inventoryHelpers";

const InventoryPage = () => {
  const default_form_values = {
    description: "",
    glass_reference: "",
    glass_type: "Bullseye",
    count: "1",
  };
  const [form_initial_values, setFormInitialValues] = useState(default_form_values);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const inventory = useFirebaseInventory();
  const all_glass_data = useAllFirebaseGlassData();
  const { createInventoryItem, editInventoryItem, deleteInventoryItem } = useInventoryActions();

  const changeQuantity = (increment: number, id: string) => {
    const new_item: any = { ...inventory?.[id] };
    const count = Number(new_item.count);
    const current = Number.isNaN(count) ? 0 : count;
    const next = current + increment;
    // Never write a quantity below 1. Pressing minus at 1 offers to remove the entry.
    if (next < 1) {
      setDeleteTargetId(id);
      return;
    }
    new_item.count = next.toString();
    editInventoryItem(id, new_item);
  };

  const confirmDelete = () => {
    if (deleteTargetId) {
      deleteInventoryItem(deleteTargetId);
    }
    setDeleteTargetId(null);
  };

  const deleteTargetItem = deleteTargetId ? inventory?.[deleteTargetId] : null;

  const editRow = (id: any) => {
    setFormInitialValues(inventory?.[id] as any);
  };

  const enriched_inventory_array = useMemo(() => {
    if (!inventory) return null;
    return Object.values(inventory).map((item) => {
      const new_item = { ...item };
      if (new_item.glass_reference) {
        const glass_item = (all_glass_data as any)?.[item.glass_type]?.inventory?.[item.glass_reference!];
        new_item.description = new_item.description
          ? glass_item?.description + "-" + new_item.description
          : glass_item?.description;
        new_item.type = glass_item?.type;
        new_item.color = glass_item?.color;
      }
      return new_item;
    });
  }, [inventory, all_glass_data]);

  const filtered_inventory = useMemo(() => {
    if (!enriched_inventory_array) return null;
    if (!searchTerm.trim()) return enriched_inventory_array;
    const term = searchTerm.toLowerCase();
    return enriched_inventory_array.filter((item) =>
      (item.glass_reference ?? "").toLowerCase().includes(term) ||
      (item.glass_type ?? "").toLowerCase().includes(term) ||
      (item.description ?? "").toLowerCase().includes(term) ||
      (item.type ?? "").toLowerCase().includes(term)
    );
  }, [enriched_inventory_array, searchTerm]);

  const sorted_inventory = useMemo(() => {
    if (!filtered_inventory || !sortField) return null;
    const arr = [...filtered_inventory];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const valA = a[sortField] ?? "";
      const valB = b[sortField] ?? "";
      if (sortField === "count") {
        return dir * (Number(valA) - Number(valB));
      }
      return dir * String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
    });
    return arr;
  }, [filtered_inventory, sortField, sortDir]);

  const grouped_inventory = useMemo(() => {
    if (!filtered_inventory || sortField) return null;
    return groupInventoryItems(filtered_inventory as EnrichedInventoryItem[]);
  }, [filtered_inventory, sortField]);

  const handleSubmit = (formValues: any) => {
    if (!(form_initial_values as any).id) {
      createInventoryItem(formValues);
    } else {
      editInventoryItem((form_initial_values as any).id, formValues);
    }
    setFormInitialValues(default_form_values);
  };

  const handleSort = (field: any) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }: { field: any }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? (
      <ArrowUp className="inline-block h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="inline-block h-3 w-3 ml-1" />
    );
  };

  const renderRow = (row: EnrichedInventoryItem) => (
    <TableRow key={row.id}>
      <TableCell>
        <img src={glassImage(row.glass_type)} alt="" className="h-8 w-8 object-contain" />
      </TableCell>
      <TableCell>{row.glass_reference}</TableCell>
      <TableCell>
        {(row.color as any)?.multicolored ? (
          <span className="text-sm">Multicoloured</span>
        ) : (
          row.color && (
            <Square
              className="inline-block h-5 w-5 fill-current"
              style={{ color: getHexColor((row.color as any)?.rgb) }}
            />
          )
        )}
      </TableCell>
      <TableCell className="whitespace-normal break-words min-w-[10rem]">{row.type}</TableCell>
      <TableCell className="whitespace-normal break-words max-w-[32rem]">{row.description}</TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-1">
          <Minus className="h-4 w-4 cursor-pointer shrink-0" onClick={() => changeQuantity(-1, row.id!)} />
          <span className="w-6 text-center">{row.count}</span>
          <Plus className="h-4 w-4 cursor-pointer shrink-0" onClick={() => changeQuantity(1, row.id!)} />
        </span>
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-3">
          <Pencil
            className="inline-block h-4 w-4 cursor-pointer"
            aria-label="Edit item"
            onClick={() => editRow(row.id)}
          />
          <Trash2
            className="inline-block h-4 w-4 cursor-pointer text-destructive"
            aria-label="Delete item"
            onClick={() => setDeleteTargetId(row.id!)}
          />
        </span>
      </TableCell>
    </TableRow>
  );

  return (
    // On wide screens the page breaks out of the app's centered max-w-5xl container to use
    // the otherwise-wasted side whitespace, so the wide inventory table has room. Centered
    // and capped; no effect below xl, so other/narrow layouts are unchanged.
    <div className="space-y-6 xl:relative xl:left-1/2 xl:-translate-x-1/2 xl:w-[min(84rem,100vw-3rem)]">
      <PageHeader
        title="Inventory"
        actions={
          <Link to="/inventory/print">
            <Button variant="outline">
              <Printer className="mr-1 inline-block h-4 w-4" />
              Print
            </Button>
          </Link>
        }
      />
      <div className="relative w-64">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search inventory..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8"
        />
      </div>
      {filtered_inventory && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => handleSort("glass_type")}>
                Manufacturer <SortIcon field="glass_type" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("glass_reference")}>
                Code <SortIcon field="glass_reference" />
              </TableHead>
              <TableHead>Colour</TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("type")}>
                Type <SortIcon field="type" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("description")}>
                Description <SortIcon field="description" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("count")}>
                Quantity <SortIcon field="count" />
              </TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortField && sorted_inventory
              ? sorted_inventory.map(renderRow)
              : grouped_inventory?.map((group) => (
                  <Fragment key={`${group.glassType}-${group.category}`}>
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted font-semibold text-sm py-2">
                        {group.glassType} — {group.category}
                      </TableCell>
                    </TableRow>
                    {group.items.map(renderRow)}
                  </Fragment>
                ))
            }
          </TableBody>
        </Table>
      )}
      <InlineMaterialForm onSubmit={handleSubmit} initialValues={form_initial_values} />
      <Dialog open={!!deleteTargetId} onOpenChange={(open) => !open && setDeleteTargetId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove inventory item?</DialogTitle>
            <DialogDescription>
              {deleteTargetItem
                ? `This will permanently remove ${
                    [deleteTargetItem.glass_type, deleteTargetItem.glass_reference, deleteTargetItem.description]
                      .filter(Boolean)
                      .join(" ") || "this item"
                  } from your inventory.`
                : "This will permanently remove this item from your inventory."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTargetId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
